import { useEffect, useState } from "react";
import { api } from "../../api/client";
import {
  PageTitle, Button, Icon, DataCard, Th, Td, Spinner, Empty,
  Modal, Field, Input, Select, cx
} from "../../components/ui";

const DAYS = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
const DAY_SHORT = ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"];
// Urutan tampil dimulai Senin — itu cara orang di sini membaca minggu. Nilai yang
// DISIMPAN tetap 0=Minggu..6=Sabtu, sama dengan Date.getDay() dan class_schedules,
// supaya tak ada dua penomoran hari di satu sistem.
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

const parseDays = (v) =>
  String(v || "").split(",").map((x) => x.trim()).filter((x) => x !== "").map(Number);
const daysLabel = (v) => {
  const d = parseDays(v);
  if (!d.length) return "";
  if (d.length === 7) return "Setiap hari";
  return DAY_ORDER.filter((x) => d.includes(x)).map((x) => DAY_SHORT[x]).join("\u00b7");
};

// Ringkas jadwal jadi chip: hari-hari dgn trainer sama digabung ("Sen·Rab·Jum — Ms. Muthi").
/**
 * Pemilih SESI. Sesi bukan sekadar label — ia ikut jadi kunci baris `attendance_recap`,
 * jadi salah ketik satu karakter memecah riwayat absensi & gaji. Karena itu sesi DIPILIH
 * dari yang sudah dipakai di cabang ini; mengetik sesi baru tetap mungkin, tapi harus
 * disengaja lewat opsi "sesi baru".
 */
function SessionPicker({ value, options = [], localOptions = null, onChange }) {
  const dikenal = options.includes(value);
  const [manual, setManual] = useState(!dikenal && !!value);
  if (manual || !options.length) {
    return (
      <div className="space-y-1">
        <Input placeholder="mis. 18.45 - 20.15" value={value} onChange={(e) => onChange(e.target.value)} />
        {!!options.length && (
          <button type="button" className="text-[11px] font-bold text-primary underline"
            onClick={() => { setManual(false); onChange(options[0]); }}>
            pilih dari sesi yang sudah ada
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <Select value={value} onChange={(e) => {
        if (e.target.value === "__baru__") { setManual(true); onChange(""); return; }
        onChange(e.target.value);
      }}>
        <option value="">— pilih sesi —</option>
        {(() => {
          // Sesi cabang ini didahulukan; sesi cabang lain tetap bisa dipilih supaya admin
          // tak perlu mengetik ulang (dan salah ejaan) saat membuka slot jam yang baru.
          // Array kosong itu truthy — pakai panjangnya, bukan keberadaannya, supaya
          // sebelum data sempat dimuat semua sesi tak salah masuk kelompok "cabang lain".
          const lokal = localOptions && localOptions.length ? localOptions : options;
          const lain = options.filter((o) => !lokal.includes(o));
          if (!lain.length) return options.map((o) => <option key={o} value={o}>{o}</option>);
          return (
            <>
              <optgroup label="Dipakai di cabang ini">
                {lokal.map((o) => <option key={o} value={o}>{o}</option>)}
              </optgroup>
              <optgroup label="Dipakai di cabang lain">
                {lain.map((o) => <option key={o} value={o}>{o}</option>)}
              </optgroup>
            </>
          );
        })()}
        <option value="__baru__">+ sesi baru…</option>
      </Select>
      <p className="text-[11px] text-slate-400">
        Sesi ikut menentukan baris absensi &amp; gaji — pilih yang sudah ada bila memungkinkan.
      </p>
    </div>
  );
}

// Trainer kelas. Hari SENGAJA tidak disebut di sini kecuali kelasnya memang diajar
// bergantian. Baris hari di `class_schedules` sebagian besar sisa backfill dari
// `students.schedule` yang 96% kosong — menampilkannya sebagai "jadwal" membuat
// admin percaya pada hari yang tak pernah ada yang mengisinya. Hari kelas jalan
// punya kolomnya sendiri sekarang (`classes.days`), dan itu yang diisi manusia.
function trainerSummary(schedules = []) {
  const uniq = [...new Map(schedules.map((s) => [s.trainerId, s])).values()];
  if (!uniq.length) return [];
  if (uniq.length === 1) {
    return [{ trainer: uniq[0].trainerName, code: uniq[0].trainerCode }];
  }
  // Dua trainer atau lebih → harinya baru benar-benar menentukan siapa mengajar.
  const dayRows = schedules.filter((s) => s.dayOfWeek !== null).sort((a, b) => a.dayOfWeek - b.dayOfWeek);
  const def = schedules.find((s) => s.dayOfWeek === null);
  const out = dayRows.map((s) => ({
    label: DAY_SHORT[s.dayOfWeek], trainer: s.trainerName, code: s.trainerCode
  }));
  if (def) out.push({ label: "hari lain", trainer: def.trainerName, code: def.trainerCode });
  return out;
}

// Manajemen Kelas (Fase 1). Kelas = entitas nyata (roster + trainer per hari).
// Perubahan di sini DITULIS-BALIK ke students.trainer/sessions → sistem lama tetap konsisten.
export default function ClassesPage({ user, activeBranch, setToast }) {
  const [rows, setRows] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [manageId, setManageId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState("");
  const [cmp, setCmp] = useState(null); // hasil shadow-compare roster (Fase 2b)
  // Tulis-balik AKTIF = perubahan kelas ikut menulis students.trainer/sessions.
  const [writeback, setWriteback] = useState(false);
  // Roster absensi sudah bersumber dari enrollments? Kalau ya, mengubah kelas LANGSUNG
  // mengubah siapa yang muncul di halaman Absensi — walau tulis-balik ke `students` mati.
  const [classRoster, setClassRoster] = useState(false);
  // Sesi yang sudah dipakai di cabang ini. Sesi adalah KUNCI baris absensi & gaji,
  // jadi UI menawarkannya sebagai pilihan supaya tak lagi rawan salah ketik.
  const [sessions, setSessions] = useState([]);
  const [sessionsLokal, setSessionsLokal] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [levels, setLevels] = useState([]);
  const [filterProgram, setFilterProgram] = useState("");
  const [hanyaTanpaHari, setHanyaTanpaHari] = useState(false);
  // Berapa kelas yang programnya belum diisi — ditampilkan sbg sisa pekerjaan.
  const belumDiatur = rows.filter((c) => !c.program).length;
  const belumBerhari = rows.filter((c) => !parseDays(c.days).length).length;
  // Kunci "Mode Tinjau" global sudah DIBUANG: ia menghalangi pekerjaan sehari-hari admin
  // (mengisi program/level pun harus buka kunci dulu) padahal tak menambah keamanan — aksi
  // yang benar-benar berbahaya kini dijaga SATU PER SATU, di server:
  //   • hapus kelas berisi murid  → ditolak 409 (hanya super admin boleh menembus)
  //   • ganti sesi saat sudah ada absensi bulan ini → ditolak 409 + konfirmasi
  //   • enroll murid beda cabang  → ditolak
  //   • semua aksi ber-:id        → dibatasi cabang (loadClassScoped, fail-closed)
  // Penjaga yang menyasar tepat sasaran lebih baik daripada gembok menyeluruh yang
  // membuat orang terbiasa membukanya tanpa berpikir.
  const isSuper = !!user?.isSuperAdmin;
  const toast = (type, message) => setToast && setToast({ type, message });

  async function load() {
    setLoading(true);
    try {
      const [cls, trn, ses, lvl] = await Promise.all([
        api.getClasses(), api.getClassTrainers(), api.getClassSessions(), api.getClassLevels()
      ]);
      if (cls?.success) {
        setRows(cls.data || []);
        setWriteback(!!cls.writeback);
        setClassRoster(!!cls.classRoster);
      }
      if (trn?.success) setTrainers(trn.data || []);
      if (ses?.success) {
        setSessions(ses.data || []);
        // `lokal` = sesi yang dipakai cabang aktif; dipakai memisahkan dua optgroup.
        setSessionsLokal(ses.lokal || ses.data || []);
      }
      if (lvl?.success) { setPrograms(lvl.programs || []); setLevels(lvl.levels || []); }
    } catch (err) {
      toast("error", err.message || "Gagal memuat kelas");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [activeBranch?.id]);

  async function runBackfill() {
    if (!window.confirm("Susun kelas otomatis dari data murid?\nAman dijalankan berulang, dan data murid tidak diubah.")) return;
    try {
      const reset = window.confirm(
        "Mulai dari nol?\n\nOK = hapus semua kelas lalu susun ulang. Kelas yang sudah Anda rapikan akan hilang.\nCancel = hanya menambah kelas yang belum ada (pilihan aman)."
      );
      const res = await api.backfillClasses(reset);
      if (!res?.success) return toast("error", res?.message || "Gagal menyusun kelas");
      const r = res.report || {};
      const un = (r.unresolvedTrainers || []).length
        ? ` · Nama trainer tak dikenali: ${r.unresolvedTrainers.join(", ")}`
        : "";
      const nod = (r.classesWithoutDays || []).length
        ? ` · ${r.classesWithoutDays.length} kelas belum punya hari (atur manual di Kelola)`
        : "";
      toast("success", `Selesai: ${r.classesCreated} kelas baru, ${r.enrollmentsAdded} murid dimasukkan, ${r.daysAdded || 0} hari diatur.${un}${nod}`);
      load();
    } catch (err) {
      toast("error", err.message || "Gagal menyusun kelas");
    }
  }

  // Fase 2b - shadow compare: bandingkan roster lama vs versi-kelas (READ-ONLY).
  async function verifyRoster() {
    const d = new Date();
    try {
      const res = await api.compareClassRoster({
        year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate()
      });
      if (res?.success) setCmp(res);
      else toast("error", res?.message || "Gagal memeriksa daftar murid");
    } catch (err) {
      toast("error", err.message || "Gagal memeriksa daftar murid");
    }
  }

  async function removeClass(c, force = false) {
    if (!force) {
      const isi = c.rosterCount
        ? `\n\nKelas ini berisi ${c.rosterCount} murid — mereka akan hilang dari halaman Absensi trainernya.`
        : "";
      if (!window.confirm(`Hapus kelas "${c.name}"?${isi}`)) return;
    }
    try {
      const res = await api.deleteClass(c.id, { force });
      // Server menahan penghapusan kelas yang masih berisi murid. Hanya super admin boleh
      // menembus, dan SERVER yang memutuskan itu (bolehPaksa) — bukan UI.
      if (res?.butuhKonfirmasi && !force) {
        if (res.bolehPaksa && window.confirm(`${res.message}\n\nTetap hapus sekarang?`)) {
          return removeClass(c, true);
        }
        toast("error", res.message);
        return;
      }
      toast(res?.success ? "success" : "error", res?.message || "Terhapus");
      if (res?.success) load();
    } catch (err) {
      toast("error", err.message || "Gagal menghapus");
    }
  }

  // Nonaktifkan / aktifkan kembali. INILAH yang seharusnya dipakai untuk kelas yang
  // berakhir — bukan tombol hapus. Bedanya: nonaktif menyimpan baris pendaftaran
  // muridnya, jadi kelasnya bisa dihidupkan lagi tanpa mendaftar ulang satu per satu;
  // hapus membuang baris itu selamanya. Riwayat absensi & gaji tak tersentuh keduanya.
  async function toggleAktif(c) {
    const jadiAktif = !c.isActive;
    if (!jadiAktif) {
      const isi = c.rosterCount
        ? `\\n\\n${c.rosterCount} murid di dalamnya berhenti muncul di halaman Absensi trainernya. Absensi & gaji yang sudah tercatat tidak berubah, dan kelasnya bisa diaktifkan lagi kapan saja.`
        : "";
      if (!window.confirm(`Nonaktifkan kelas "${c.name}"?${isi}`)) return;
    }
    try {
      const res = await api.updateClass(c.id, { isActive: jadiAktif });
      toast(
        res?.success ? "success" : "error",
        res?.success
          ? (jadiAktif ? "Kelas diaktifkan kembali." : "Kelas dinonaktifkan.")
          : (res?.message || "Gagal mengubah status kelas")
      );
      if (res?.success) load();
    } catch (err) { toast("error", err.message); }
  }

  // Cari juga lewat KODE trainer — kode itulah yang dipegang admin di berkas HR.
  const visible = rows.filter((c) => {
    // Filter program dicocokkan PERSIS (kolom tersendiri), bukan lewat pencarian teks —
    // itulah gunanya program & level dipisah jadi dua kolom.
    // "__kosong__" = kelas yang programnya BELUM diisi; dipakai admin cabang menelusuri
    // sisa pekerjaan sampai habis, tanpa perlu memelototi satu per satu.
    if (hanyaTanpaHari && parseDays(c.days).length) return false;
    if (filterProgram === "__kosong__") { if (c.program) return false; }
    else if (filterProgram && c.program !== filterProgram) return false;
    return [c.name, c.session, c.program, c.level, c.room, c.trainerText,
      ...(c.schedules || []).map((s) => s.trainerCode)]
      .join(" ").toLowerCase().includes(query.toLowerCase());
  });

  return (
    <>
      <PageTitle
        title="Manajemen Kelas"
        subtitle="Atur daftar murid tiap kelas dan siapa trainernya di hari apa."
        action={
          <div className="flex flex-wrap gap-2">
            <Input placeholder="Cari kelas / trainer / kode..." value={query} onChange={(e) => setQuery(e.target.value)} />
            {!!programs.length && (
              <Select value={filterProgram} onChange={(e) => setFilterProgram(e.target.value)} title="Saring per program">
                <option value="">Semua program</option>
                <option value="__kosong__">— belum diatur —</option>
                {programs.map((p) => <option key={p} value={p}>{p}</option>)}
              </Select>
            )}
            {isSuper && (
              <Button variant="secondary" onClick={runBackfill} title="Susun kelas otomatis dari data murid">
                <Icon name="autorenew" />
                Susun Otomatis
              </Button>
            )}
            <Button variant="secondary" onClick={verifyRoster} title="Cek apakah daftar murid di kelas sama dengan data murid">
              <Icon name="fact_check" />
              Cek Daftar Murid
            </Button>
            <Button onClick={() => setCreating(true)}>
              <Icon name="add" />
              Buat Kelas
            </Button>
          </div>
        }
      />

      {classRoster || writeback ? (
        <div className="mb-4 flex items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-400">
          <Icon name="bolt" className="text-base" />
          <p>
            <b>Perubahan di sini langsung dipakai halaman Absensi.</b>{" "}
            Mengganti trainer atau mengeluarkan murid mengubah siapa yang muncul di absensi hari itu.
          </p>
        </div>
      ) : (
        <div className="mb-4 flex items-center gap-3 rounded-xl border border-sky-500/25 bg-sky-500/5 p-3 text-sm text-sky-700 dark:text-sky-300">
          <Icon name="construction" className="text-base" />
          <p><b>Masih tahap persiapan</b> — perubahan di sini belum memengaruhi absensi maupun gaji.</p>
        </div>
      )}

      {/* Dulu bertuliskan "ini hanya label, aman diisi". Sejak To-Do List kelas,
          Program MENENTUKAN bank aktivitas yang dilihat trainer — jadi kelas yang
          Program-nya kosong membuat trainernya tak punya pilihan sama sekali. */}
      {!loading && !!rows.length && belumDiatur > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-indigo-500/25 bg-indigo-500/5 p-3 text-sm text-indigo-700 dark:text-indigo-300">
          <Icon name="checklist" className="text-base" />
          <p className="flex-1">
            <span className="font-black">{belumDiatur} dari {rows.length} kelas</span> belum punya
            <b> Program</b>. Isi lewat tombol <b>Kelola</b> &mdash; Program menentukan pilihan
            aktivitas yang muncul di <b>To-Do List</b> trainer, jadi kelas yang kosong membuat
            trainernya tak bisa memilih apa pun.
          </p>
          <Button
            variant="secondary"
            onClick={() => setFilterProgram(filterProgram === "__kosong__" ? "" : "__kosong__")}
          >
            {filterProgram === "__kosong__" ? "Tampilkan semua" : "Tampilkan yang belum"}
          </Button>
        </div>
      )}

      {!loading && !!rows.length && belumBerhari > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-700 dark:text-amber-300">
          <Icon name="edit_calendar" className="text-base" />
          <p className="flex-1">
            <span className="font-black">{belumBerhari} dari {rows.length} kelas</span> belum diisi
            <b> hari jalannya</b>. Selama kosong, kelas itu belum bisa ikut dipantau di halaman
            Pemantauan Absensi harian &mdash; dan itu bukan berarti kelasnya libur.
          </p>
          <Button
            variant="secondary"
            onClick={() => setHanyaTanpaHari((v) => !v)}
          >
            {hanyaTanpaHari ? "Tampilkan semua" : "Tampilkan yang belum"}
          </Button>
        </div>
      )}

      {loading ? (
        <Spinner label="Memuat kelas..." />
      ) : !visible.length ? (
        <Empty label={rows.length ? "Tak ada kelas cocok." : "Belum ada kelas di cabang ini. Super admin bisa menekan Susun Otomatis untuk membuatnya dari data murid."} />
      ) : (
        <>
        {/* Layar lebar: tabel. Layar kecil: kartu di bawah — tabel 760px tak terbaca
            di HP, padahal admin cabang menyisir puluhan kelas untuk mengisi Program. */}
        <DataCard className="hidden md:block">
          <div className="max-h-[560px] overflow-auto">
            <table className="w-full min-w-[880px] text-left">
              {/* Judul kolom ikut menempel saat digulir — satu cabang bisa punya ~30 kelas,
                  dan tanpa ini kolom PROGRAM/SESI/TRAINER jadi tak terbaca di tengah daftar.
                  Pola yang sama sudah dipakai di Recap & Absensi Harian. */}
              <thead className="sticky top-0 z-20">
                <tr>
                  {["KELAS", "PROGRAM / LEVEL", "SESI", "HARI", "TRAINER", "MURID", "AKSI"].map((h) => (
                    <Th key={h} right={h === "AKSI"}>{h}</Th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((c) => (
                  <tr key={c.id} className="border-t border-slate-200 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-primary/5">
                    <Td>
                      <p className="font-black">{c.name}</p>
                      <p className="text-[11px] text-slate-400">
                        {c.branchCode}
                        {c.room ? ` · ${c.room}` : ""}
                        {c.isActive ? "" : " · nonaktif"}
                      </p>
                    </Td>
                    <Td>
                      {c.program || c.level ? (
                        <div className="flex flex-wrap items-center gap-1">
                          {c.program && (
                            <span className="rounded bg-indigo-500/15 px-1.5 py-0.5 text-[10px] font-black text-indigo-600 dark:text-indigo-400">
                              {c.program}
                            </span>
                          )}
                          {c.level && (
                            <span className="rounded bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-black text-sky-600 dark:text-sky-400">
                              {c.level}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-[11px] text-slate-300">—</span>
                      )}
                      {!c.isActive && (
                        <span className="ml-1 rounded bg-slate-500/15 px-1.5 py-0.5 text-[10px] font-black text-slate-500">
                          NONAKTIF
                        </span>
                      )}
                    </Td>
                    <Td mono>{c.session}</Td>
                    <Td>
                      {parseDays(c.days).length ? (
                        <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-black text-emerald-600 dark:text-emerald-400">
                          {daysLabel(c.days)}
                        </span>
                      ) : (
                        <span
                          title="Kelas ini belum muncul di Pemantauan Absensi harian. Isi lewat Kelola."
                          className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-black text-amber-600 dark:text-amber-400"
                        >
                          Belum diisi
                        </span>
                      )}
                    </Td>
                    <Td>
                      {trainerSummary(c.schedules).length ? (
                        <div className="flex flex-wrap gap-1">
                          {trainerSummary(c.schedules).map((s, i) => (
                            <span key={i} className="rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-bold text-primary">
                              {s.label ? `${s.label} — ` : ""}{s.trainer}
                              {s.code ? <span className="ml-1 font-mono opacity-60">{s.code}</span> : null}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-[11px] font-bold text-amber-600">Belum ada trainer</span>
                      )}
                    </Td>
                    <Td>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-black dark:bg-slate-800">
                        {c.rosterCount}
                      </span>
                    </Td>
                    <Td right>
                      <div className="flex justify-end gap-2">
                        <Button variant="secondary" onClick={() => setManageId(c.id)}>
                          <Icon name="tune" />
                          Kelola
                        </Button>
                        <Button
                          variant="secondary"
                          title={c.isActive
                            ? "Nonaktifkan — berhenti muncul di absensi, tapi muridnya tetap tersimpan"
                            : "Aktifkan kembali"}
                          onClick={() => toggleAktif(c)}
                        >
                          <Icon name={c.isActive ? "toggle_on" : "toggle_off"} />
                        </Button>
                        <Button variant="danger" onClick={() => removeClass(c)}>
                          <Icon name="delete" />
                        </Button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DataCard>

        <div className="space-y-3 md:hidden">
          {visible.map((c) => (
            <ClassCard key={c.id} c={c} onManage={setManageId} onRemove={removeClass} onToggle={toggleAktif} />
          ))}
        </div>
        </>
      )}

      <CreateClassModal
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={() => { setCreating(false); load(); }}
        setToast={setToast}
        sessionOptions={sessions}
        sessionLocalOptions={sessionsLokal}
        programOptions={programs}
        levelOptions={levels}
      />

      {cmp && (
        <Modal title={`Cek Daftar Murid \u2014 ${cmp.tanggal}`} open onClose={() => setCmp(null)}>
          <div className={cx(
            "rounded-xl p-4 text-sm font-bold",
            cmp.bersih
              ? "bg-green-500/10 text-green-700 dark:text-green-400"
              : "bg-amber-500/10 text-amber-700 dark:text-amber-400"
          )}>
            {cmp.bersih
              ? "\u2713 Daftar murid di kelas sudah sama persis dengan data murid, termasuk jam sesinya."
              : `\u26a0 Ada perbedaan pada ${cmp.ringkasan.trainerBerselisih} trainer. Sebaiknya dirapikan.`}
          </div>
          <div className="mt-3 grid grid-cols-4 gap-2 text-center text-xs">
            {[["Trainer diperiksa", cmp.ringkasan.trainerDicek], ["Cocok", cmp.ringkasan.cocok],
              ["Hanya di data murid", cmp.ringkasan.hanyaLama], ["Hanya di kelas", cmp.ringkasan.hanyaBaru]].map(([l, v]) => (
              <div key={l} className="rounded-lg bg-slate-100 p-2 dark:bg-slate-800">
                <p className="text-[10px] font-bold uppercase text-slate-400">{l}</p>
                <p className="text-lg font-black">{v}</p>
              </div>
            ))}
          </div>
          {!!cmp.selisih.length && (
            <div className="mt-4 max-h-64 space-y-2 overflow-auto">
              {cmp.selisih.map((d, i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-3 text-xs dark:border-slate-700">
                  <p className="font-black">{d.trainer} <span className="font-medium text-slate-400">cocok {d.sama}</span></p>
                  {!!d.jmlHanyaLama && <p className="mt-1 text-amber-600">Ada di data murid, belum masuk kelas ({d.jmlHanyaLama}): {d.contohHanyaLama.join(", ")}</p>}
                  {!!d.jmlHanyaBaru && <p className="mt-1 text-sky-600">Ada di kelas, tak ada di data murid ({d.jmlHanyaBaru}): {d.contohHanyaBaru.join(", ")}</p>}
                </div>
              ))}
            </div>
          )}
          <p className="mt-3 text-[11px] text-slate-500 dark:text-slate-400">
            Diperiksa per murid <b>dan</b> per jam sesinya. Pemeriksaan ini hanya membaca — tidak
            mengubah apa pun. Halaman Absensi saat ini mengambil muridnya dari{" "}
            <b className={cmp.sumberRoster === "classes" ? "text-sky-600 dark:text-sky-400" : ""}>
              {cmp.sumberRoster === "classes" ? "data kelas" : "data murid"}
            </b>.
          </p>
        </Modal>
      )}

      {manageId != null && (
        <ManageClassModal
          classId={manageId}
          sessionOptions={sessions}
          sessionLocalOptions={sessionsLokal}
          programOptions={programs}
          levelOptions={levels}
          trainers={trainers}
          onClose={() => setManageId(null)}
          onSaved={load}
          setToast={setToast}
        />
      )}
    </>
  );
}

// Kartu kelas untuk layar HP. Isinya SAMA dengan baris tabel, disusun menurun:
// nama & ruang, lencana program/level, sesi, trainer per hari, jumlah murid, lalu aksi.
function ClassCard({ c, onManage, onRemove, onToggle }) {
  const jadwal = trainerSummary(c.schedules);
  return (
    <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900/40">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-black leading-tight">{c.name}</p>
          <p className="mt-0.5 text-[11px] text-slate-400">
            {c.branchCode}
            {c.room ? ` \u00b7 ${c.room}` : ""}
            {c.isActive ? "" : " \u00b7 nonaktif"}
          </p>
        </div>
        <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-black dark:bg-slate-800">
          {c.rosterCount} murid
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-1">
        {c.program && (
          <span className="rounded bg-indigo-500/15 px-1.5 py-0.5 text-[10px] font-black text-indigo-600 dark:text-indigo-400">
            {c.program}
          </span>
        )}
        {c.level && (
          <span className="rounded bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-black text-sky-600 dark:text-sky-400">
            {c.level}
          </span>
        )}
        {!c.program && !c.level && (
          <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-black text-amber-600 dark:text-amber-400">
            Program belum diisi
          </span>
        )}
        <span className="ml-auto font-mono text-xs font-bold text-slate-500">{c.session}</span>
      </div>

      <div>
        <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Hari kelas jalan</p>
        <div className="mt-1">
          {parseDays(c.days).length ? (
            <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-black text-emerald-600 dark:text-emerald-400">
              {daysLabel(c.days)}
            </span>
          ) : (
            <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-black text-amber-600 dark:text-amber-400">
              Belum diisi
            </span>
          )}
        </div>
      </div>

      <div>
        <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Trainer</p>
        {jadwal.length ? (
          <div className="mt-1 flex flex-wrap gap-1">
            {jadwal.map((s, i) => (
              <span key={i} className="rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-bold text-primary">
                {s.label ? `${s.label} — ` : ""}{s.trainer}
                {s.code ? <span className="ml-1 font-mono opacity-60">{s.code}</span> : null}
              </span>
            ))}
          </div>
        ) : (
          <p className="mt-1 text-[11px] font-bold text-amber-600">Belum ada trainer</p>
        )}
      </div>

      <div className="flex gap-2 pt-1">
        <Button variant="secondary" className="flex-1" onClick={() => onManage(c.id)}>
          <Icon name="tune" />
          Kelola
        </Button>
        <Button
          variant="secondary"
          className="px-3"
          onClick={() => onToggle(c)}
          title={c.isActive ? "Nonaktifkan kelas" : "Aktifkan kembali"}
        >
          <Icon name={c.isActive ? "toggle_on" : "toggle_off"} />
        </Button>
        <Button variant="danger" className="px-3" onClick={() => onRemove(c)} title="Hapus kelas">
          <Icon name="delete" />
        </Button>
      </div>
    </div>
  );
}

function CreateClassModal({ open, onClose, onSaved, setToast, sessionOptions = [], sessionLocalOptions = null, programOptions = [], levelOptions = [] }) {
  const empty = { name: "", session: "", program: "", level: "", room: "" };
  const [form, setForm] = useState(empty);
  useEffect(() => { if (open) setForm(empty); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open]);

  async function save() {
    if (!form.session.trim()) return setToast && setToast({ type: "error", message: "Sesi wajib diisi." });
    try {
      const res = await api.createClass({
        name: form.name.trim(), session: form.session.trim(),
        program: form.program, level: form.level, room: form.room.trim()
      });
      setToast && setToast({ type: res?.success ? "success" : "error", message: res?.message || "Tersimpan" });
      if (res?.success) onSaved();
    } catch (err) {
      setToast && setToast({ type: "error", message: err.message || "Gagal menyimpan" });
    }
  }

  return (
    <Modal title="Buat Kelas" open={open} onClose={onClose}>
      <div className="grid gap-4">
        <Field label="Sesi (wajib)">
          <SessionPicker
            value={form.session}
            options={sessionOptions}
            localOptions={sessionLocalOptions}
            onChange={(v) => setForm({ ...form, session: v })}
          />
        </Field>
        <Field label="Nama kelas (opsional — otomatis bila kosong)">
          <Input placeholder="otomatis dari cabang · sesi" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Program">
            <Select value={form.program} onChange={(e) => setForm({ ...form, program: e.target.value })}>
              <option value="">— belum diatur —</option>
              {programOptions.map((p) => <option key={p} value={p}>{p}</option>)}
            </Select>
          </Field>
          <Field label="Level (opsional)">
            <Select value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })}>
              <option value="">— tanpa level —</option>
              {levelOptions.map((l) => <option key={l} value={l}>{l}</option>)}
            </Select>
          </Field>
          <Field label="Ruang / lokasi (opsional)">
            <Input placeholder="mis. R-1" value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} />
          </Field>
        </div>
      </div>
      <Button className="mt-5 w-full" onClick={save}>Simpan</Button>
    </Modal>
  );
}

function ManageClassModal({ classId, trainers, sessionOptions = [], sessionLocalOptions = null, programOptions = [], levelOptions = [], onClose, onSaved, setToast }) {
  const [detail, setDetail] = useState(null);
  const [meta, setMeta] = useState({ name: "", session: "", program: "", level: "", room: "", isActive: true });
  const [defTrainer, setDefTrainer] = useState("");
  const [dayMap, setDayMap] = useState({}); // { [hari 0..6]: trainerId(string) }
  const [students, setStudents] = useState([]);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  // Murid yang masih terdaftar di kelas lain pada jam yang sama — menunggu keputusan admin.
  const [bentrok, setBentrok] = useState(null);
  const toast = (type, message) => setToast && setToast({ type, message });

  async function load() {
    try {
      const [d, all] = await Promise.all([api.getClassDetail(classId), api.getAllStudents()]);
      if (d?.success) {
        setDetail(d.data);
        setMeta({
          name: d.data.name, session: d.data.session,
          days: d.data.days || "",
          program: d.data.program || "", level: d.data.level || "",
          room: d.data.room || "", isActive: d.data.isActive
        });
      }
      if (all?.success) setStudents(all.data || []);
      const list = (await api.getClasses())?.data || [];
      const me = list.find((x) => x.id === classId);
      const sch = (me && me.schedules) || [];
      setDefTrainer(String((sch.find((s) => s.dayOfWeek === null) || {}).trainerId || ""));
      const dm = {};
      sch.filter((s) => s.dayOfWeek !== null).forEach((s) => { dm[s.dayOfWeek] = String(s.trainerId); });
      setDayMap(dm);
    } catch (err) {
      toast("error", err.message || "Gagal memuat detail kelas");
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [classId]);

  // SATU tombol simpan untuk seluruh dialog. Dulu terpisah dua ("Simpan info kelas"
  // dan "Simpan jadwal trainer"), dan itu membuat admin mengira sudah menyimpan
  // padahal baru separuh — kesalahan yang tak terlihat sampai absensinya keliru
  // keesokan harinya. Daftar murid tetap tersimpan seketika (tombolnya sendiri),
  // karena itu memang tindakan langsung, bukan isian formulir.
  //
  // Urutannya: info kelas DULU. Hanya dia yang bisa menahan diri — perubahan sesi
  // yang memecah riwayat absensi bulan berjalan butuh penegasan admin.
  async function simpanSemua(force = false) {
    setBusy(true);
    try {
      const res = await api.updateClass(classId, meta, { force });
      if (res?.butuhKonfirmasi && !force) {
        const lanjut = window.confirm(
          `${res.message}\\n\\nLanjutkan tetap mengubah sesi ke "${res.sesiBaru}"?`
        );
        setBusy(false);
        if (lanjut) return simpanSemua(true);
        toast("error", "Dibatalkan — tidak ada yang tersimpan.");
        return;
      }
      if (!res?.success) { toast("error", res?.message || "Gagal menyimpan kelas"); return; }

      const list = [];
      if (defTrainer) list.push({ dayOfWeek: null, trainerId: Number(defTrainer) });
      Object.entries(dayMap).forEach(([d, t]) => {
        if (t) list.push({ dayOfWeek: Number(d), trainerId: Number(t) });
      });
      const res2 = await api.setClassSchedules(classId, list);
      if (!res2?.success) {
        // Info kelas SUDAH tersimpan — jangan bilang semuanya gagal.
        toast("error", `Info kelas tersimpan, tapi trainernya gagal: ${res2?.message || "tak diketahui"}`);
        onSaved(); load();
        return;
      }
      toast("success", "Perubahan kelas tersimpan.");
      onSaved(); load();
    } catch (err) { toast("error", err.message); } finally { setBusy(false); }
  }

  // `pilihan` hanya dikirim setelah server menahan enroll karena jamnya bentrok:
  // "pindah" = keluarkan dari kelas lama, "tetap" = tetap di dua kelas.
  async function addStudent(sid, pilihan) {
    try {
      const res = await api.enrollClassStudents(classId, [sid], pilihan ? { bentrok: pilihan } : {});
      setBentrok(null);
      if (res?.success) { toast("success", res.message); load(); onSaved(); }
      else toast("error", res?.message);
    } catch (err) {
      if (err?.data?.kode === "sesi-bentrok") {
        const b = (err.data.bentrok || [])[0] || {};
        setBentrok({ sid, nama: b.nama || sid, kelas: b.kelas || [], sesi: err.data.sesi || "" });
        return;
      }
      toast("error", err.message);
    }
  }

  async function removeStudent(sid) {
    // Satu-satunya aksi di modal ini yang tak punya penjaga di server, padahal akibatnya
    // langsung: murid hilang dari halaman Absensi trainernya. Karena kunci menyeluruh
    // sudah dibuang, konfirmasinya dipasang tepat di sini — lengkap dengan akibatnya.
    const m = (detail?.students || []).find((x) => x.studentId === sid);
    if (!window.confirm(
      `Keluarkan ${m ? m.name : sid} dari kelas ini?\n\n` +
      "Murid ini akan hilang dari halaman Absensi trainernya. Data muridnya sendiri tidak dihapus."
    )) return;
    try {
      const res = await api.unenrollClassStudent(classId, sid);
      if (res?.success) { toast("success", res.message); load(); onSaved(); }
      else toast("error", res?.message);
    } catch (err) { toast("error", err.message); }
  }

  const enrolled = new Set((detail?.students || []).map((s) => s.studentId));
  const candidates = pick.trim()
    ? students
        .map((s) => ({ id: s.studentId || s.student_id, name: s.name }))
        .filter((s) => s.id && !enrolled.has(s.id) && `${s.id} ${s.name}`.toLowerCase().includes(pick.toLowerCase()))
        .slice(0, 8)
    : [];

  return (
    <Modal title={detail ? `Kelola: ${detail.name}` : "Kelola Kelas"} open onClose={onClose}>
      {!detail ? (
        <Spinner label="Memuat..." />
      ) : (
        <div className="space-y-6">
          {/* ── Info kelas ── */}
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Nama kelas">
              <Input value={meta.name} onChange={(e) => setMeta({ ...meta, name: e.target.value })} />
            </Field>
            <Field label="Sesi">
              <SessionPicker
                value={meta.session}
                options={sessionOptions}
                localOptions={sessionLocalOptions}
                onChange={(v) => setMeta({ ...meta, session: v })}
              />
            </Field>
            <Field label="Program">
              <Select value={meta.program || ""} onChange={(e) => setMeta({ ...meta, program: e.target.value })}>
                <option value="">— belum diatur —</option>
                {programOptions.map((p) => <option key={p} value={p}>{p}</option>)}
              </Select>
            </Field>
            <Field label="Level (opsional)">
              <Select value={meta.level || ""} onChange={(e) => setMeta({ ...meta, level: e.target.value })}>
                <option value="">— tanpa level —</option>
                {levelOptions.map((l) => <option key={l} value={l}>{l}</option>)}
              </Select>
            </Field>
            <Field label="Ruang / lokasi">
              <Input placeholder="mis. R-1" value={meta.room} onChange={(e) => setMeta({ ...meta, room: e.target.value })} />
            </Field>
          </div>

          {/* ── Hari kelas ini jalan ──
              Beda dengan "Trainer per hari" di bawah: yang ini menjawab APAKAH kelasnya
              jalan hari itu, bukan SIAPA yang mengajar. Dipakai halaman Pemantauan untuk
              membedakan kelas yang belum diabsen dari kelas yang hari itu memang libur. */}
          <div className="rounded-xl border border-slate-200 p-4 dark:border-primary/15">
            <p className="text-sm font-black">Hari kelas ini jalan</p>
            <p className="mb-3 text-[11px] text-slate-400">
              Dipakai untuk memantau absensi harian. Selama belum diisi, kelas ini tidak
              bisa ikut dipantau &mdash; bukan berarti dianggap libur.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {DAY_ORDER.map((d) => {
                const aktif = parseDays(meta.days).includes(d);
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => {
                      const set = new Set(parseDays(meta.days));
                      if (set.has(d)) set.delete(d); else set.add(d);
                      setMeta({ ...meta, days: [...set].sort((a, b) => a - b).join(",") });
                    }}
                    className={cx(
                      "rounded-lg border px-3 py-1.5 text-xs font-bold transition",
                      aktif
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-slate-200 text-slate-400 hover:border-primary/50 dark:border-slate-700"
                    )}
                  >
                    {DAY_SHORT[d]}
                  </button>
                );
              })}
            </div>
            {!parseDays(meta.days).length && (
              <p className="mt-2 text-[11px] font-bold text-amber-600 dark:text-amber-400">
                Belum diisi &mdash; kelas ini belum muncul di pemantauan absensi harian.
              </p>
            )}
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-xs font-bold text-slate-500">
            <input type="checkbox" checked={meta.isActive} onChange={(e) => setMeta({ ...meta, isActive: e.target.checked })} className="rounded text-primary focus:ring-primary" />
            Kelas aktif
          </label>
          {/* ── Trainer per hari ── */}
          <div className="rounded-xl border border-slate-200 p-4 dark:border-primary/15">
            <p className="text-sm font-black">Trainer</p>
            <p className="mb-3 text-[11px] text-slate-400">
              Trainer default berlaku semua hari. Tambah override bila hari tertentu diajar trainer lain.
            </p>
            <Field label="Trainer default (semua hari)">
              <Select value={defTrainer} onChange={(e) => setDefTrainer(e.target.value)}>
                <option value="">— belum diatur —</option>
                {trainers.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}{t.code ? ` · ${t.code}` : ""}</option>
                ))}
              </Select>
            </Field>

            <p className="mt-4 text-xs font-black uppercase tracking-wide text-slate-500">Hari kelas</p>
            <p className="mb-2 text-[11px] text-slate-400">
              Centang hari kelas ini berjalan. Trainer boleh beda tiap hari (default dipakai bila tak diubah).
            </p>
            <div className="space-y-1.5">
              {DAYS.map((label, d) => {
                const on = dayMap[d] !== undefined;
                return (
                  <div key={d} className="flex flex-wrap items-center gap-2">
                    <label className="flex w-28 cursor-pointer items-center gap-2 text-xs font-bold">
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={(e) => {
                          const next = { ...dayMap };
                          if (e.target.checked) next[d] = defTrainer || "";
                          else delete next[d];
                          setDayMap(next);
                        }}
                        className="rounded text-primary focus:ring-primary"
                      />
                      {label}
                    </label>
                    {on && (
                      <Select
                        className="w-44"
                        value={dayMap[d]}
                        onChange={(e) => setDayMap({ ...dayMap, [d]: e.target.value })}
                      >
                        <option value="">— pilih trainer —</option>
                        {trainers.map((t) => (
                          <option key={t.id} value={t.id}>{t.name}{t.code ? ` · ${t.code}` : ""}</option>
                        ))}
                      </Select>
                    )}
                  </div>
                );
              })}
            </div>

          </div>

          {/* ── Roster ── */}
          <div className="rounded-xl border border-slate-200 p-4 dark:border-primary/15">
            <p className="text-sm font-black">Daftar Murid ({detail.students.length})</p>

            <div className="mt-3">
              <Input placeholder="Cari murid untuk ditambahkan..."
                value={pick} onChange={(e) => setPick(e.target.value)} />
              {candidates.map((s) => (
                <button
                  key={s.id}
                  onClick={() => { addStudent(s.id); setPick(""); }}
                  className="mt-1 flex w-full items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-left text-xs font-bold hover:border-primary/40 dark:border-slate-700"
                >
                  <span>{s.name} <span className="text-slate-400">· {s.id}</span></span>
                  <Icon name="add" className="text-primary" />
                </button>
              ))}
            </div>

            {/* Murid masih di kelas lain pada JAM YANG SAMA. Absensi disimpan per (murid,
                sesi), jadi dua kelas berjam sama berbagi satu baris absensi dan muridnya
                muncul di dua kartu sekaligus. Biasanya ini naik level yang lupa dikeluarkan
                dari kelas lamanya — karena itu pilihan pertamanya "pindahkan". */}
            {bentrok && (
              <div role="alert" className="mt-3 rounded-xl border border-amber-300 bg-amber-500/10 p-3 text-xs dark:border-amber-500/40">
                <p className="font-black text-amber-700 dark:text-amber-300">
                  {bentrok.nama} masih terdaftar di kelas{" "}
                  {bentrok.kelas.map((k) => k.trainerText || k.name).join(" & ") || "lain"} pada jam
                  yang sama{bentrok.sesi ? ` (${bentrok.sesi})` : ""}.
                </p>
                <p className="mt-1 text-slate-500 dark:text-slate-400">
                  Di dua kelas berjam sama, namanya muncul di dua absensi sekaligus. Kalau ia naik
                  level atau pindah kelas, pindahkan saja &mdash; hari-hari di kelas lamanya tetap
                  tercatat di kelas lama.
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Button onClick={() => addStudent(bentrok.sid, "pindah")}>
                    Pindahkan ke kelas ini
                  </Button>
                  <Button variant="secondary" onClick={() => addStudent(bentrok.sid, "tetap")}>
                    Tetap di dua kelas
                  </Button>
                  <button type="button" onClick={() => setBentrok(null)}
                    className="px-2 text-xs font-bold text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">
                    Batal
                  </button>
                </div>
              </div>
            )}

            <div className="mt-3 max-h-56 space-y-1 overflow-auto">
              {detail.students.map((s) => (
                <div key={s.studentId} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-xs dark:bg-slate-800/40">
                  <span className="font-bold">
                    {s.name} <span className="text-slate-400">· {s.studentId}</span>
                    {s.status !== "aktif" && <span className="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-black text-amber-600">{s.status}</span>}
                  </span>
                  <button onClick={() => removeStudent(s.studentId)}
                    className="text-slate-400 hover:text-red-500 disabled:opacity-30">
                    <Icon name="close" />
                  </button>
                </div>
              ))}
              {!detail.students.length && <p className="text-xs text-slate-400">Belum ada murid.</p>}
            </div>
            <p className="mt-2 text-[11px] text-slate-400">
              Menambah &amp; mengeluarkan murid langsung tersimpan — tak perlu menekan Simpan.
            </p>
          </div>

          <div className="sticky bottom-0 -mx-1 flex items-center gap-3 border-t border-slate-200 bg-white/95 px-1 py-3 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
            <p className="flex-1 text-[11px] text-slate-400">
              Info kelas, hari, dan trainer tersimpan sekaligus.
            </p>
            <Button disabled={busy} onClick={() => simpanSemua()}>
              <Icon name="save" />
              {busy ? "Menyimpan..." : "Simpan Perubahan"}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
